import React, { useState } from 'react';
import Axios from 'axios';
import Select from '@/components/common/ReactSelect5';
import * as Routes from '@/modules/routes';
import showToast from '@/components/common/Toast';
import useProgramAccessStore from './useProgramAccessStore';
import { ISeatPool, IUserType } from './utils';
import styles from './GradeDropdown.module.scss';

type GradeOption = {
  label: string;
  value: string;
};

const GRADE_OPTIONS: GradeOption[] = [
  { label: 'None', value: '' },
  ...['K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'].map(grade => ({
    label: grade,
    value: grade,
  })),
];

interface GradeDropdownProps {
  seatPool: ISeatPool;
  userType: IUserType;
}

const GradeDropdown = ({ seatPool, userType }: GradeDropdownProps) => {
  const updateSeatPoolGrade = useProgramAccessStore(store => store.updateSeatPoolGrade);
  const [currentGrade, setCurrentGrade] = useState(seatPool.grade || '');
  const [isLoading, setIsLoading] = useState(false);

  if (userType === 'Coordinator') {
    return <span>{currentGrade || 'None'}</span>;
  }

  const handleChange = (selectedOption: GradeOption | null) => {
    const previousGrade = currentGrade;
    const newGrade = selectedOption?.value || '';

    setCurrentGrade(newGrade);
    setIsLoading(true);

    Axios.patch(Routes.plato_api_seat_pool_path(seatPool.id), {
      seat_pool: { grade: newGrade || null },
    })
      .then(() => {
        updateSeatPoolGrade(seatPool.id, newGrade);
        showToast('Grade level successfully changed.', { iconStyle: 'circle', position: 'bottom-right' });
      })
      .catch(() => {
        setCurrentGrade(previousGrade);
        showToast('Grade level could not be changed. Please try again.',
          { autoClose: 10000, iconStyle: 'circle', msgType: 'error', position: 'bottom-right' });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <Select
      aria-label="Grade"
      className="tw-text-sm tw-min-w-[90px]"
      classNamePrefix="react-select"
      classNames={{ menuList: () => styles.menuList }}
      isDisabled={isLoading}
      isLoading={isLoading}
      options={GRADE_OPTIONS}
      value={GRADE_OPTIONS.find(o => o.value === currentGrade) || null}
      onChange={handleChange}
    />
  );
};

export default GradeDropdown;
